import { isEqual, pick } from 'lodash';

import { PaymentStrategy } from '@ctct/state-server-layer';

import type { Controller, Listeners } from '../helpers';
import { controllerHelpers } from '../helpers';
import type {
  AlertManager,
  BillingInfoFormValues,
  OrderSummaryManager,
  PaymentButtonManager,
  PaymentMethodsManager,
  UpdateBillingManager,
} from '../managers';
import {
  paymentMethodsEventBus,
  billingInfoFormEventBus,
  APPLEPAY_VALUES_AVAILABLE_FOR_BILLING_INFO_FORM,
  orderSummaryEventBus,
} from '../managers';

export class PaymentMethodsController implements Controller {
  public listeners: Listeners;

  constructor(
    private paymentMethods: PaymentMethodsManager,
    private alertManager: AlertManager,
    private updateBilling?: UpdateBillingManager,
    private orderSummary?: OrderSummaryManager,
    private paymentButtonManager?: PaymentButtonManager
  ) {
    const eventConnections = [
      paymentMethodsEventBus.bindTo('paypalConnected', this.onPaypalConnected),
      paymentMethodsEventBus.bindTo('paypalError', this.onPaypalError),
      paymentMethodsEventBus.bindTo('amazonPayConnected', this.updateInfo),
      paymentMethodsEventBus.bindTo('paymentMethodCanBeUpdated', this.onPaymentMethodCanBeUpdated),
      paymentMethodsEventBus.bindTo('applePayIFrameLoaded', this.onApplePayIFrameLoaded),
      paymentMethodsEventBus.bindTo('applePayAborted', this.onApplePayAborted),
      paymentMethodsEventBus.bindTo('applePayTaxValueRequest', this.onApplePayTaxValueRequest),
      paymentMethodsEventBus.bindTo('stripePostRedirectSuccess', this.onStripePostRedirectSuccess),
      paymentMethodsEventBus.bindTo('stripePostRedirectFailure', this.onStripePostRedirectFailure),
      billingInfoFormEventBus.bindTo('missingFields', this.billingInfoFormFieldsMissing),
      billingInfoFormEventBus.bindTo('formDirtyStateChanged', this.onBillingInfoFormDirtyStateChanged),
      orderSummaryEventBus.bindTo('paymentDataUpdated', this.updateApplePayPaymentData),
      paymentMethodsEventBus.bindTo('applePayConnected', this.onApplePayConnected),
      paymentMethodsEventBus.bindTo('stripePaymentMethodChanged', this.onStripePaymentMethodChanged),
    ];

    this.listeners = controllerHelpers.createListeners(eventConnections);
  }

  private onPaypalConnected = async ({ isStripe }: { isStripe: boolean }) => {
    if (isStripe) {
      this.paymentButtonManager?.getApiWhenReady().then((api) => api.completeCheckout());

      return;
    }

    this.updateInfo();
  };

  private onPaypalError = async ({ error }: { error: string }) => {
    const alertApi = await this.alertManager.getApiWhenReady();

    alertApi.addAlert({
      message: error,
      id: error,
    });
  };

  private onApplePayConnected = async () => {
    this.paymentButtonManager?.getApiWhenReady().then((api) => api.onApplePayMobileAutoCheckout());
  };

  private onStripePostRedirectSuccess = async () => {
    this.paymentButtonManager?.getApiWhenReady().then((api) => api.completeCheckout());
  };

  private onStripePostRedirectFailure = async ({ message }: { message: string }) => {
    const alertApi = await this.alertManager.getApiWhenReady();

    alertApi.addAlert({
      message,
      id: message,
    });
  };

  private updateInfo = () => {
    this.updateBilling?.getApiWhenReady().then((api) => api.requestUpdateInfo());
  };

  private onPaymentMethodCanBeUpdated = async (canBeUpdated: boolean) => {
    if (this.updateBilling) {
      const api = await this.updateBilling.getApiWhenReady();

      api.reportPaymentCanBeUpdated(canBeUpdated);
    }
  };

  private onApplePayIFrameLoaded = async () => {
    const orderSummaryApi = await this.orderSummary?.getApiWhenReady();
    const rating = orderSummaryApi?.getRating();

    if (rating) {
      const payload = {
        amount: rating.getAmountToCharge().priceAmount,
        taxAmount: rating.getTotalTax().priceAmount,
        currency: rating.getCurrency(),
      };

      this.updateApplePayPaymentData(payload);
    }
  };

  private onApplePayAborted = async () => {
    if (this.orderSummary) {
      const api = await this.orderSummary?.getApiWhenReady();

      api.setTaxUpdateRequested(false);
    }
  };

  private onApplePayTaxValueRequest = async (address) => {
    if (this.orderSummary) {
      const api = await this.orderSummary?.getApiWhenReady();
      const adress = address.addressLine.join(' ');

      const applePayTaxInfo = {
        // if there is no address coming from apple pay, we set value of 'unknown', as we need to have non-empty string as some temporary value to get tax value
        address1: adress.length ? adress : 'unknown',
        city: address.city,
        zipcode: address.postalCode,
        country: address.country,
        state: address.region,
      };

      const currentTaxInfo = pick(api.getTaxData(), ['address1', 'city', 'zipcode', 'country', 'state']);

      // if tax info from apple pay is the same, we need to cancel tax update request, otherwise apple pay will wait form new rating call, which will not be performed, since tax data has not changed
      if (isEqual(currentTaxInfo, applePayTaxInfo)) {
        const paymentMethodsApi = await this.paymentMethods.getApiWhenReady();

        if (paymentMethodsApi.paymentStrategy === PaymentStrategy.Paas) {
          paymentMethodsApi.setApplePayTaxValue(null);
        }
      }

      api.setTaxUpdateRequested(true);
      api.updateCustomerPartially(applePayTaxInfo);
    }
  };

  private billingInfoFormFieldsMissing = async (missingFields: (keyof BillingInfoFormValues)[]) => {
    const canGetMissingValuesFromApplePay = missingFields.some((item) =>
      APPLEPAY_VALUES_AVAILABLE_FOR_BILLING_INFO_FORM.includes(item)
    );
    const api = await this.paymentMethods.getApiWhenReady();

    if (api.paymentStrategy === PaymentStrategy.Paas) {
      api.setApplePayShouldGetAddressWhenLoaded(canGetMissingValuesFromApplePay);

      if (this.orderSummary) {
        api.setApplePayShouldGetTaxUpdateWhenLoaded(canGetMissingValuesFromApplePay);
      }
    }
  };

  private onBillingInfoFormDirtyStateChanged = async (isFormDirty: boolean) => {
    const api = await this.paymentMethods.getApiWhenReady();

    if (api.paymentStrategy === PaymentStrategy.Paas && isFormDirty) {
      api.setApplePayShouldGetAddressWhenLoaded(false);
      api.setApplePayShouldGetTaxUpdateWhenLoaded(false);
    }
  };

  private updateApplePayPaymentData = async (payload) => {
    const [orderSummaryApi, paymentMethodsApi] = await Promise.all([
      this.orderSummary?.getApiWhenReady(),
      this.paymentMethods.getApiWhenReady(),
    ]);

    if (paymentMethodsApi.paymentStrategy === PaymentStrategy.Paas) {
      const isApplePayLoaded = paymentMethodsApi.getIsApplePayLoaded();
      const hasNewApplePayToken = paymentMethodsApi.getHasNewApplePayToken();

      if (!isApplePayLoaded && !hasNewApplePayToken) {
        // if apple pay is not loaded (for example not selected as payment method - skip setting the payment data)
        return;
      }

      if (hasNewApplePayToken) {
        paymentMethodsApi.setApplePayTotalChanged(payload);

        return;
      }

      const isTaxUpdateRating = orderSummaryApi?.getTaxUpdateRequested();

      if (isTaxUpdateRating) {
        paymentMethodsApi.setApplePayTaxValue(payload.taxAmount);
      }

      paymentMethodsApi.setApplePayPaymentData(payload);
    }
  };

  private onStripePaymentMethodChanged = async ({ paymentMethod }: { paymentMethod: string }) => {
    if (this.paymentButtonManager) {
      const api = await this.paymentButtonManager.getApiWhenReady();

      api.setStripePaymentMethod(paymentMethod);
    }
  };
}
